"use client";

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { cn } from "@/lib/utils";
import Image from "next/image";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";

const Placements3DataSchema = z.object({
  status: z
    .union([z.boolean(), z.number()])
    .transform((v) => Boolean(v))
    .optional()
    .default(true),
  id: z.union([z.number(), z.string()]).optional(),
  page_id: z.number().optional(),
  component_id: z.number().optional(),
  variant: z.string().optional(),
  background_color: z.string().optional().default("#FFFFFF"),
  row1_image: z.string().optional().default(""),
  row2_image: z.string().optional().default(""),
  row1_scroll_frequency: z.union([z.string(), z.number()]).optional().default(3000),
  row2_scroll_frequency: z.union([z.string(), z.number()]).optional().default(3000),
  row1_scroll_direction: z.enum(["left", "right"]).optional().default("left"),
  row2_scroll_direction: z.enum(["left", "right"]).optional().default("right"),
});

type Placements3Data = z.infer<typeof Placements3DataSchema>;

const ScrollingRow: FC<{
  imageUrl: string;
  frequency: number;
  direction: "left" | "right";
  alt?: string;
  rowClassName?: string;
  backgroundColor?: string;
}> = ({ imageUrl, frequency, direction, alt = "Placement partners", rowClassName, backgroundColor }) => {
  /** CMS stores ms per loop; avoid Math.max(10, …) which ignored values under 10000ms. */
  const ms = Number(frequency);
  const safeMs = Number.isFinite(ms) && ms > 0 ? ms : 3000;
  const durationSec = Math.max(0.5, safeMs / 1000);
  const isRight = direction === "right";
  const containerRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const animId = useRef(`p3m${Math.random().toString(36).slice(2, 8)}`).current;
  const [copies, setCopies] = useState(5);
  const [scrollDist, setScrollDist] = useState(0);

  const recalc = useCallback(() => {
    const container = containerRef.current;
    const copy = copyRef.current;
    if (!container || !copy) return;
    const vw = container.clientWidth;
    const cw = copy.offsetWidth;
    if (cw <= 0) return;
    const needed = Math.ceil(vw / cw) + 2;
    setCopies(Math.max(3, needed));
    setScrollDist(cw);
  }, []);

  useEffect(() => {
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [recalc]);

  return (
    <div
      ref={containerRef}
      className={cn("w-full overflow-hidden relative", rowClassName)}
    >
      <div
        className="flex items-center"
        style={{
          width: "max-content",
          ...(scrollDist > 0 && {
            animation: `${animId} ${durationSec}s linear infinite`,
          }),
        }}
      >
        {Array.from({ length: copies }, (_, i) => (
          <div
            key={i}
            ref={i === 0 ? copyRef : undefined}
            className={cn(
              "shrink-0",
              // "xs:pr-[16px]",
              // "sm:pr-[32px]",
              // "md:pr-[32px]",
              // "lg:pr-[40px]",
              // "xl:pr-[40px]"
            )}
          >
            <Image
              src={imageUrl}
              alt={alt}
              width={1200}
              height={80}
              className={cn(
                "w-auto object-contain max-w-none",
                "xs:h-[24px]",
                "sm:h-[60px]",
                "md:h-[100px]",
                "lg:h-[100px]",
                "xl:h-[100px]"
              )}
              unoptimized
              onLoad={recalc}
            />
          </div>
        ))}
      </div>
      {backgroundColor && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            backgroundImage: `linear-gradient(90deg, ${backgroundColor} 0%, transparent 25.6%, transparent 77.4%, ${backgroundColor} 100%)`,
          }}
        />
      )}
      <style>{`
        @keyframes ${animId} {
          from { transform: translateX(${isRight ? `-${scrollDist}px` : "0px"}); }
          to { transform: translateX(${isRight ? "0px" : `-${scrollDist}px`}); }
        }
      `}</style>
    </div>
  );
};

export const Placements3: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;
  const parsedResult = Placements3DataSchema.safeParse(actualData);
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(1080);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "placements_section");
  //   }
  // }, []);

  if (!parsedResult.success) {
    console.error("Placements3 validation failed", parsedResult.error);
    return null;
  }

  const data: Placements3Data = parsedResult.data;
  const {
    status,
    background_color,
    row1_image,
    row2_image,
    row1_scroll_frequency,
    row2_scroll_frequency,
    row1_scroll_direction,
    row2_scroll_direction,
  } = data;

  if (!status || isMobile === null) return null;

  const raw1 =
    typeof row1_scroll_frequency === "string"
      ? parseInt(row1_scroll_frequency, 10)
      : row1_scroll_frequency;
  const raw2 =
    typeof row2_scroll_frequency === "string"
      ? parseInt(row2_scroll_frequency, 10)
      : row2_scroll_frequency;
  const freq1 = Number.isFinite(Number(raw1)) && Number(raw1) > 0 ? Number(raw1) : 3000;
  const freq2 = Number.isFinite(Number(raw2)) && Number(raw2) > 0 ? Number(raw2) : 3000;
  const dir1 = (row1_scroll_direction === "right" ? "right" : "left") as "left" | "right";
  const dir2 = (row2_scroll_direction === "right" ? "right" : "left") as "left" | "right";

  const hasRow1 = !!row1_image?.trim();
  const hasRow2 = !!row2_image?.trim();
  if (!hasRow1 && !hasRow2) return null;

  return (
    <section
      ref={ref}
      className={cn(
        "relative w-full overflow-hidden",
        "xs:py-[10px]",
        "sm:py-[20px]",
        "md:py-[20px]",
        "lg:py-[60px]",
        "xl:py-[60px]"
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "mx-auto flex flex-col",
          "xs:gap-[12px]",
          "sm:gap-[14px]",
          "md:gap-[14px]",
          "lg:gap-[14px]",
          "xl:gap-[14px]"
        )}
      >
        {hasRow1 && (
          <ScrollingRow
            imageUrl={row1_image!}
            frequency={freq1}
            direction={dir1}
            alt="Placement partners row 1"
            rowClassName={cn(
              "xs:min-h-[40px]",
              "sm:min-h-[60px]",
              "md:min-h-[60px]",
              "lg:min-h-[70px]",
              "xl:min-h-[70px]"
            )}
            backgroundColor={background_color}
          />
        )}
        {hasRow2 && (
          <ScrollingRow
            imageUrl={row2_image!}
            frequency={freq2}
            direction={dir2}
            alt="Placement partners row 2"
            rowClassName={cn(
              "xs:min-h-[40px]",
              "sm:min-h-[60px]",
              "md:min-h-[60px]",
              "lg:min-h-[70px]",
              "xl:min-h-[70px]"
            )}
            backgroundColor={background_color}
          />
        )}
      </div>
    </section>
  );
};
